import React, { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faChartBar,
  faClipboardList,
  faMap,
  faUsers,
  faScroll,
  faCircleInfo,
  faRightFromBracket,
  faBars,
  faXmark,
  faFilePdf,
  faBoxesPacking,
  faLightbulb,
  faBolt,
  faMapPin,
} from '@fortawesome/free-solid-svg-icons';
import { useAuth } from '../../../contexts/AuthContext';
import { authService } from '../../../services/api';
import styles from './Sidebar.module.css';

interface SidebarProps {
  onToggle?: (collapsed: boolean) => void;
}

const Sidebar: React.FC<SidebarProps> = ({ onToggle }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { logout, user } = useAuth();
  const [resolvedName, setResolvedName] = useState('');
  const [collapsed, setCollapsed] = useState(true);

  const roleLabel = (role?: string) => {
    const normalized = (role || '').toUpperCase();
    if (normalized === 'ADMIN') return 'Administrador';
    if (normalized === 'TECNICO') return 'Tecnico';
    return normalized || 'Nao informado';
  };

  useEffect(() => {
    let cancelled = false;

    const resolveName = async () => {
      const fromSession = user?.nome?.trim();
      if (fromSession) {
        setResolvedName(fromSession);
        return;
      }

      if (!user?.email) {
        setResolvedName('');
        return;
      }

      try {
        const users = await authService.listUsers();
        const match = users.find(
          (u) => (u.email || '').toLowerCase() === user.email.toLowerCase(),
        );
        if (!cancelled) {
          setResolvedName((match?.nome || '').trim());
        }
      } catch {
        if (!cancelled) {
          setResolvedName('');
        }
      }
    };

    resolveName();

    return () => {
      cancelled = true;
    };
  }, [user?.email, user?.nome]);

  const displayName =
    resolvedName || user?.nome?.trim() || user?.email?.split('@')[0] || 'Usuario';

  const isMaster = (user?.role || '').toUpperCase() === 'MASTER';

  const menuItems = [
    { path: '/admin', label: 'Dashboard', icon: faChartBar, exact: true },
    { path: '/admin/orders', label: 'Ordens', icon: faClipboardList },
    { path: '/admin/relatorios', label: 'Relatórios', icon: faFilePdf },
    { path: '/admin/mapa-calor', label: 'Mapa', icon: faMap },
    ...(isMaster ? [{ path: '/admin/users', label: 'Usuários', icon: faUsers }] : []),
    ...(isMaster ? [{ path: '/admin/logs', label: 'Logs', icon: faScroll }] : []),
    ...((isMaster || (user?.role || '').toUpperCase() === 'ADMIN') ? [{ path: '/admin/materials', label: 'Materiais', icon: faBoxesPacking }] : []),
    ...((isMaster || (user?.role || '').toUpperCase() === 'ADMIN') ? [{ path: '/admin/lampadas', label: 'Lâmpadas', icon: faLightbulb }] : []),
    ...((isMaster || (user?.role || '').toUpperCase() === 'ADMIN') ? [{ path: '/admin/refletores', label: 'Refletores', icon: faBolt }] : []),
    ...((isMaster || (user?.role || '').toUpperCase() === 'ADMIN') ? [{ path: '/admin/bairros', label: 'Bairros', icon: faMapPin }] : []),
    { path: '/admin/sobre', label: 'Sobre', icon: faCircleInfo },
  ];

  const handleLogout = async () => {
    await logout();
    navigate('/login', { replace: true });
  };

  const toggleSidebar = () => {
    const newState = !collapsed;
    setCollapsed(newState);
    if (onToggle) {
      onToggle(newState);
    }
  };

  return (
    <>
      <div className={`${styles.sidebar} ${collapsed ? styles.sidebarCollapsed : ''}`}>
        <button
          type="button"
          onClick={toggleSidebar}
          className={styles.toggleButton}
          title={collapsed ? 'Expandir menu' : 'Recolher menu'}
        >
          <FontAwesomeIcon icon={collapsed ? faBars : faXmark} />
        </button>

        <div className={styles.header}>
          <h3 className={styles.title}>Admin</h3>
          {!collapsed && (
            <>
              <p className={styles.userInfo}>Ola, {displayName}</p>
              <p className={styles.userEmail}>{user?.email}</p>
              <p className={styles.userRole}>Funcao: {roleLabel(user?.role)}</p>
            </>
          )}
        </div>

        <nav className={styles.nav}>
          {menuItems.map((item) => {
            const isActive = item.exact
              ? location.pathname === item.path
              : location.pathname.startsWith(item.path) &&
                location.pathname !== '/admin';

            return (
              <Link
                key={item.path}
                to={item.path}
                className={`${styles.menuLink} ${
                  isActive ? styles.menuLinkActive : ''
                }`}
                title={collapsed ? item.label : undefined}
              >
                <span className={styles.menuIcon}>
                  <FontAwesomeIcon icon={item.icon} />
                </span>
                {!collapsed && <span className={styles.menuLabel}>{item.label}</span>}
              </Link>
            );
          })}
        </nav>

        <div className={styles.logoutContainer}>
          <img
            src="/prefeitura.png"
            alt="Prefeitura de Pesqueira"
            className={styles.prefeituraLogo}
          />
          <button type="button" onClick={handleLogout} className={styles.logoutButton}>
            {collapsed ? <FontAwesomeIcon icon={faRightFromBracket} /> : 'Sair'}
          </button>
        </div>
      </div>

      {collapsed && (
        <button
          type="button"
          onClick={toggleSidebar}
          className={styles.expandOverlay}
          title="Expandir menu"
        >
          <FontAwesomeIcon icon={faBars} />
        </button>
      )}
    </>
  );
};

export default Sidebar;
